import React from 'react';
import { Card, Row, Col, Space, theme } from 'antd';
import { isSectionEnabled } from '../utils/previewHelpers';

/**
 * Section Renderer Component - Renders a form section with column layout support
 * Supports section-level enableWhen for conditional visibility
 */
export const SectionRenderer = ({ 
  section, 
  sectionIndex, 
  renderQuestion, 
  compact = false,
  schema,
  formValues = {},
}) => {
  const { token } = theme.useToken();

  // Check section-level conditional display
  if (!isSectionEnabled(section, schema, formValues)) {
    return null;
  }

  const columnLayout = section.columnLayout || 1;
  const showTitle = section.showTitleInPreview !== false;
  const questions = section.questions || [];
  
  // Pre-render questions and filter out empty ones
  const allRendered = questions.map(renderQuestion);
  const renderedQuestions = allRendered.filter(q => {
    // Filter out null, undefined, false, and empty React fragments
    if (q === null || q === undefined || q === false) return false;
    // Check if it's a valid React element with content
    return true;
  });
  
  // Return null for empty sections - hides the entire section including title
  if (renderedQuestions.length === 0) {
    return null;
  }
  
  // Split rendered questions into columns
  const colSpan = 24 / columnLayout;
  const questionsPerColumn = Math.ceil(renderedQuestions.length / columnLayout);
  const columns = [];
  
  for (let i = 0; i < columnLayout; i++) {
    const startIdx = i * questionsPerColumn;
    const endIdx = Math.min(startIdx + questionsPerColumn, renderedQuestions.length);
    columns.push(renderedQuestions.slice(startIdx, endIdx));
  }
  
  // Filter out empty columns and adjust spans
  const nonEmptyColumns = columns.filter(col => col.length > 0);
  const actualColumnCount = nonEmptyColumns.length;
  const adjustedColSpan = actualColumnCount > 0 ? 24 / actualColumnCount : 24;
  
  return (
    <Card
      key={sectionIndex}
      size={compact ? 'small' : 'default'}
      style={{ marginBottom: compact ? 8 : 16 }}
      headStyle={showTitle ? {
        backgroundColor: token.colorFillQuaternary,
        color: token.colorText,
        padding: '4px 12px',
        minHeight: 'auto'
      } : undefined}
      title={showTitle && section.label ? (
        <span style={{ color: token.colorText }}>{section.label}</span>
      ) : undefined}
    >
      {columnLayout === 1 || actualColumnCount === 1 ? (
        renderedQuestions
      ) : (
        <Row gutter={[24, 16]}>
          {nonEmptyColumns.map((columnQuestions, colIndex) => (
            <Col key={colIndex} span={adjustedColSpan}>
              {columnQuestions}
            </Col>
          ))}
        </Row>
      )}
    </Card>
  );
};
